import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';
import { getShowById, getRelatedShows, shows } from '@/mocks/shows';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();

  const show = useMemo(() => {
    return articleId ? getShowById(articleId) : undefined;
  }, [articleId]);

  const relatedShows = useMemo(() => {
    return show ? getRelatedShows(show, 4) : [];
  }, [show]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as unknown as ScrollBehavior });
  }, [articleId]);

  useEffect(() => {
    if (!show) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${show.title} - 6789影院热门综艺节目在线观看`,
      description: show.description,
      image: show.coverImage,
      datePublished: `${show.year}-01-01`,
      dateModified: show.updateDate,
      author: {
        '@type': 'Organization',
        name: '6789影院',
      },
      publisher: {
        '@type': 'Organization',
        name: '6789影院',
        url: 'https://www.6789cinema.com/',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.6789cinema.com/article/${show.id}`,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.6789cinema.com/' },
          { '@type': 'ListItem', position: 2, name: show.category, item: `https://www.6789cinema.com/category/${show.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: show.title },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [show]);

  if (!show) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-700">节目未找到</h2>
            <p className="mt-1 text-sm text-foreground-500">该综艺节目可能已下架或链接无效</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: show.category, href: `/category/${show.categorySlug}` },
          { label: show.title },
        ]} />

        <article className="px-4 md:px-6 pb-14 md:pb-16">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                    <img
                      src={show.coverImage}
                      alt={`${show.title} - 6789影院热门综艺节目`}
                      title={`${show.title} - 6789影院综艺节目在线观看`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <header>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                      {show.rating} 分
                    </span>
                    <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {show.category}
                    </span>
                    {show.isHot && (
                      <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                        热门推荐
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                    {show.title}
                  </h1>
                </header>

                <div className="mt-6 flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    主持人：{show.host}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {show.year}年
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-play-list-2-line"></i>
                    共{show.episodeCount}期
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {show.views} 播放
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-refresh-line"></i>
                    更新于 {show.updateDate}
                  </span>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {show.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8">
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-4">
                    节目介绍
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                    {show.longDescription}
                  </p>
                </div>

                <div className="mt-8">
                  <a
                    href="#"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-base font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    立即观看
                  </a>
                </div>
              </div>
            </div>

            {relatedShows.length > 0 && (
              <section className="mt-14 md:mt-20" aria-label="相关推荐">
                <div className="mb-6">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    相关推荐
                  </h2>
                  <p className="mt-1 text-sm text-foreground-500">你可能也喜欢这些综艺节目</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                  {relatedShows.map(s => (
                    <ShowCard key={s.id} show={s} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}